import type { BroadcastFormData } from '@/pages/Meta/Broadcast/types/broadcast.types';
import {
    blocosDoGrupo,
    type RotinaGrupo,
    type RotinaGrupoPayload,
    type RotinaGrupoUpdate,
} from '@/services/api/rotinas';
import { hhmm } from './rotinaUtils';

export type Editor = {
    grupo?: RotinaGrupo;
    initialData: BroadcastFormData;
    /** Páginas do grupo fora do seletor (pausadas): ficam no molde e voltam a disparar se reativadas. */
    foraDoSeletor: string[];
};

export const FORMULARIO_VAZIO: BroadcastFormData = {
    selectedPages: [],
    blocks: [],
    isScheduled: false,
    scheduledTime: '',
    // SEM tipo de propósito: é o que distingue "grupo do zero" (abre na etapa
    // Tipo de Envio) de "grupo copiado de um broadcast", que já chega com o
    // tipo decidido e tem de pular essa etapa — escolher ali de novo zerava o
    // conteúdo que estava sendo copiado.
    broadcastType: undefined,
    messageTag: null,
    // Marcada por padrão: é o padrão de todos os títulos de hoje.
    tituloComDataHora: true,
};

const mesmasPaginas = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().every((p, i) => p === [...b].sort()[i]);

/** Só o que mudou: o PUT propaga ao que já foi criado hoje, e reenviar o que não mudou reescreveria à toa. */
export function soOQueMudou(g: RotinaGrupo, p: RotinaGrupoPayload): RotinaGrupoUpdate {
    const d: RotinaGrupoUpdate = {};
    if (p.titulo_modelo !== g.titulo_modelo) d.titulo_modelo = p.titulo_modelo;
    if (p.titulo_com_data_hora !== (g.titulo_com_data_hora !== 0)) d.titulo_com_data_hora = p.titulo_com_data_hora;
    if (p.hora_local !== hhmm(g.hora_local)) d.hora_local = p.hora_local;
    if (JSON.stringify(p.template_params.blocks) !== JSON.stringify(blocosDoGrupo(g.template_params))) {
        d.template_params = p.template_params;
    }
    if (p.message_tag !== (g.message_tag ?? null)) d.message_tag = p.message_tag;
    if (p.utility_template_id !== (g.utility_template_id ?? null)) {
        d.utility_template_id = p.utility_template_id;
    }
    if (p.secondary_utility_template_id !== (g.secondary_utility_template_id ?? null)) {
        d.secondary_utility_template_id = p.secondary_utility_template_id;
    }
    if (p.utility_esteira_id !== (g.utility_esteira_id ?? null)) {
        d.utility_esteira_id = p.utility_esteira_id;
    }
    if (p.ai_translate !== !!g.ai_translate) d.ai_translate = p.ai_translate;
    if (p.source_language !== (g.source_language ?? null)) d.source_language = p.source_language;
    if (!mesmasPaginas(p.paginas, g.paginas)) d.paginas = p.paginas;
    return d;
}

/** O formulário do CreateBroadcastModal preenchido com o grupo que já existe. */
export function formularioDoGrupo(
    grupo: RotinaGrupo,
    selectedPages: BroadcastFormData['selectedPages'],
): BroadcastFormData {
    return {
        title: grupo.titulo_modelo,
        tituloComDataHora: grupo.titulo_com_data_hora !== 0,
        horaLocal: hhmm(grupo.hora_local),
        selectedPages,
        blocks: blocosDoGrupo(grupo.template_params),
        isScheduled: false,
        scheduledTime: '',
        // O tipo vem do que esta gravado, na ordem em que o backend grava:
        // esteira e utility sao excludentes entre si e com a tag.
        broadcastType: grupo.utility_esteira_id
            ? 'esteira'
            : grupo.utility_template_id
                ? 'utility'
                : grupo.message_tag ? 'message_tag' : 'standard',
        messageTag: grupo.message_tag,
        utilityTemplateId: grupo.utility_template_id ?? undefined,
        secondaryUtilityTemplateId: grupo.secondary_utility_template_id ?? undefined,
        selectedEsteiraId: grupo.utility_esteira_id,
        aiTranslate: !!grupo.ai_translate,
        sourceLanguage: grupo.source_language ?? undefined,
    };
}
